// Advanced Calculator — companion to Calculator.jsx
// Features (all new, no impact on existing Calculator):
//  - Region select (Gulf / Medium / Africa)
//  - Wheel select (all wheel IDs)
//  - "Completed draws" easy mode  +  per-draw Toggle advanced mode
//  - Discount % field
//  - Current CP balance
//  - Multi-wheel aggregator with add / remove / clear
//  - Share / copy result
//  - Smart package suggestion on grand total
//
// Calls backend /api/calculator/calc  — single source of truth (same as RUSH AI).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { publicApi, formatApiError } from "../lib/api";
import { ChevronDown, CopyIcon, Loader2, Plus, Send, ShoppingCart, Trash2, X } from "lucide-react";
import "./AdvancedCalculator.css";

const REGIONS = [
  { id: "gulf", label: "الخليجي" },
  { id: "medium", label: "المتوسط" },
  { id: "africa", label: "الأفريقي" },
];

const fmt = (n) => Number(n || 0).toLocaleString("en-US");
const fmtSar = (n) => (Number.isInteger(Number(n)) ? `${n} ر.س` : `${Number(n).toFixed(2)} ر.س`);

const AdvancedCalculator = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("calc"); // calc | mythic
  const [region, setRegion] = useState("gulf");
  const [wheels, setWheels] = useState([]);
  const [wheelId, setWheelId] = useState("");
  const [totalDraws, setTotalDraws] = useState(10);
  const [drawStates, setDrawStates] = useState([]); // single: {included:bool}[]
  const [completed, setCompleted] = useState(0);
  const [currentCP, setCurrentCP] = useState(0);
  const [discountPct, setDiscountPct] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false); // collapsible per-draw customization
  const [result, setResult] = useState(null);
  const [combo, setCombo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [aggregate, setAggregate] = useState([]);

  const currentWheel = useMemo(() => wheels.find((w) => w.id === wheelId), [wheels, wheelId]);
  const isDual = !!currentWheel?.is_dual;
  // Filter dual wheels out — they have dedicated pages
  const singleWheels = useMemo(() => wheels.filter((w) => !w.is_dual), [wheels]);

  const initStatesFor = (w) => {
    if (!w) return [];
    return Array(w.total_draws).fill({ included: true });
  };

  // Handle wheel selection — dual wheels redirect to dedicated pages
  const handleWheelChange = (id) => {
    if (id === "dual_weapons") {
      navigate("/dual-weapons");
      return;
    }
    if (id === "dual_characters") {
      navigate("/dual-characters");
      return;
    }
    setWheelId(id);
  };

  // Load wheel list for region
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await publicApi.get(`/calculator/wheels?region=${region}`);
        if (cancelled) return;
        setWheels(data.wheels || []);
        const singles = (data.wheels || []).filter((w) => !w.is_dual);
        if (singles.length) {
          const stillExists = singles.some((w) => w.id === wheelId);
          if (!stillExists) {
            const w0 = singles[0];
            setWheelId(w0.id);
            setTotalDraws(w0.total_draws);
            setDrawStates(initStatesFor(w0));
          }
        }
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region]);

  // Reset draw states when wheel changes
  useEffect(() => {
    const w = wheels.find((x) => x.id === wheelId);
    if (w) {
      setTotalDraws(w.total_draws);
      setDrawStates(initStatesFor(w));
      setCompleted(0);
      setShowAdvanced(false);
    }
  }, [wheelId, wheels]);

  const excludedDraws = useMemo(() => {
    if (!showAdvanced) return [];
    return drawStates.map((s, i) => (s?.included ? null : i + 1)).filter((x) => x !== null);
  }, [drawStates, showAdvanced]);

  const runCalc = useCallback(async () => {
    if (!wheelId || isDual) return;
    setLoading(true);
    setError("");
    try {
      const body = {
        wheel_id: wheelId,
        region,
        completed_draws: !showAdvanced ? Math.max(0, Math.min(Number(completed) || 0, totalDraws)) : 0,
        excluded_draws: showAdvanced ? excludedDraws : [],
        current_cp: Math.max(0, Number(currentCP) || 0),
        discount_pct: Math.max(0, Math.min(Number(discountPct) || 0, 100)),
      };
      const { data } = await publicApi.post("/calculator/calc", body);
      setResult(data);
      if (data.ok && data.net_needed_cp > 0) {
        const { data: cd } = await publicApi.post("/calculator/best-packages", { needed_cp: data.net_needed_cp });
        setCombo(cd);
      } else {
        setCombo(null);
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [wheelId, region, completed, showAdvanced, excludedDraws, currentCP, discountPct, totalDraws, isDual]);

  useEffect(() => {
    if (wheelId && !isDual) runCalc();
  }, [runCalc, wheelId, isDual]);

  const toggleDraw = (i) => {
    setDrawStates((prev) => prev.map((s, idx) => (idx === i ? { included: !s.included } : s)));
  };

  const allOn = () => setDrawStates(Array(totalDraws).fill({ included: true }));
  const allOff = () => setDrawStates(Array(totalDraws).fill({ included: false }));

  const addToAggregate = () => {
    if (!result?.ok) return;
    setAggregate((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${prev.length}`,
        wheelName: result.wheel.name,
        regionLabel: result.region_label,
        remaining: result.remaining_cp,
        netNeeded: result.net_needed_cp,
      },
    ]);
  };

  const removeAgg = (id) => setAggregate((prev) => prev.filter((x) => x.id !== id));
  const clearAgg = () => setAggregate([]);

  const [aggCombo, setAggCombo] = useState(null);
  const aggTotal = useMemo(() => aggregate.reduce((s, x) => s + x.remaining, 0), [aggregate]);

  useEffect(() => {
    if (!aggTotal) return setAggCombo(null);
    (async () => {
      try {
        const { data } = await publicApi.post("/calculator/best-packages", { needed_cp: aggTotal });
        setAggCombo(data);
      } catch {}
    })();
  }, [aggTotal]);

  const buildShareText = () => {
    const lines = ["🧮 حاسبة RUSH STORE", ""];
    if (result?.ok) {
      lines.push(`📌 ${result.wheel.name}`);
      lines.push(`📌 الحساب: ${result.region_label}`);
      if (result.completed_draws) lines.push(`📌 السحبات المنفذة: ${result.completed_draws}`);
      if (result.excluded_draws?.length) lines.push(`📌 المستبعدة: ${result.excluded_draws.join(", ")}`);
      // Dual: summarize per-draw states
      if (result.is_dual && Array.isArray(result.draw_states)) {
        const regs = result.draw_states.map((s, i) => s === "regular" ? i + 1 : null).filter(Boolean);
        const advs = result.draw_states.map((s, i) => s === "advanced" ? i + 1 : null).filter(Boolean);
        const sks  = result.draw_states.map((s, i) => s === "skip" ? i + 1 : null).filter(Boolean);
        if (regs.length) lines.push(`📌 اعتيادي: ${regs.join(", ")}`);
        if (advs.length) lines.push(`📌 متقدم: ${advs.join(", ")}`);
        if (sks.length)  lines.push(`📌 مسحوبة: ${sks.join(", ")}`);
      }
      if (result.discount_pct) lines.push(`📌 خصم: ${Math.round(result.discount_pct)}%`);
      lines.push("");
      lines.push(`💠 CP المطلوب`);
      lines.push(`${fmt(result.remaining_cp)} CP`);
      if (result.current_cp) {
        lines.push("");
        lines.push(`💰 رصيدك: ${fmt(result.current_cp)} CP`);
        lines.push(`✅ الصافي: ${fmt(result.net_needed_cp)} CP`);
      }
    }
    if (combo?.ok && combo.combos.length) {
      lines.push("");
      lines.push("━━━━━━━━━━━━━━");
      lines.push("");
      lines.push("💎 أفضل تركيبة");
      for (const it of combo.combos[0].items) {
        const q = it.qty > 1 ? ` × ${it.qty}` : "";
        lines.push(`${fmt(it.cp)} CP${q}`);
        lines.push(`💰 ${fmtSar(it.sar * it.qty)}`);
      }
      lines.push(`📦 المجموع: ${fmt(combo.combos[0].total_cp)} CP`);
      lines.push(`💰 الإجمالي: ${fmtSar(combo.combos[0].total_sar)}`);
    }
    lines.push("");
    lines.push("🛒 https://salla.sa/rushcp.com");
    return lines.join("\n");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildShareText());
    } catch {}
  };

  const handleWhatsapp = () => {
    const t = encodeURIComponent(buildShareText());
    window.open(`https://wa.me/?text=${t}`, "_blank", "noopener");
  };

  return (
    <section id="advanced-calc" dir="rtl" data-testid="advanced-calculator" className="adv-calc max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#C9A96A]/[0.08] border border-[#C9A96A]/25 text-[#D4B98A] text-[11px] font-medium mb-3">
          <span>حاسبة CP · RUSH STORE</span>
        </div>
        <h2 className="text-white text-2xl sm:text-3xl font-black tracking-tight">الحاسبة</h2>
        <p className="text-white/50 text-sm mt-2">اختر الحساب · العجلة · حالة كل سحبة · وشاهد التكلفة فورًا</p>
      </div>

      {/* Top tabs: Calculator | Mythic Development */}
      <div className="flex items-center gap-2 mb-4 justify-center" role="tablist" aria-label="التبويبات">
        <button
          type="button"
          role="tab"
          data-testid="adv-tab-calc"
          aria-selected={tab === "calc"}
          onClick={() => setTab("calc")}
          className={`px-4 py-2 rounded-xl text-xs font-black border transition-colors ${
            tab === "calc"
              ? "bg-[#C9A96A]/20 border-[#C9A96A]/60 text-[#F5EAC9]"
              : "bg-white/[0.02] border-white/10 text-slate-300 hover:border-[#C9A96A]/25"
          }`}
        >
          🧮 حاسبة العجلات
        </button>
        <button
          type="button"
          role="tab"
          data-testid="adv-tab-mythic"
          aria-selected={tab === "mythic"}
          onClick={() => setTab("mythic")}
          className={`px-4 py-2 rounded-xl text-xs font-black border transition-colors ${
            tab === "mythic"
              ? "bg-[#C9A96A]/20 border-[#C9A96A]/60 text-[#F5EAC9]"
              : "bg-white/[0.02] border-white/10 text-slate-300 hover:border-[#C9A96A]/25"
          }`}
        >
          ⚔️ تطوير الميثيك
        </button>
      </div>

      {tab === "mythic" ? (
        <div className="adv-calc__card" data-testid="adv-mythic-panel">
          <div className="text-center py-6">
            <div className="inline-flex w-14 h-14 rounded-2xl bg-[#C9A96A]/10 border border-[#C9A96A]/30 items-center justify-center mb-3 text-2xl">⚔️</div>
            <h3 className="text-[#F5EAC9] text-lg font-black">حاسبة تطوير الأسلحة/الشخصيات الميثيك</h3>
            <p className="text-white/60 text-sm mt-2 max-w-md mx-auto leading-6">
              نعمل حاليًا على جمع بيانات ترقية الميثيك <strong>الموثّقة</strong> بشكل كامل (كروت التطوير لكل مستوى، مصادر الحصول، ومعدل الاستهلاك).
            </p>
            <p className="text-white/50 text-xs mt-2 max-w-md mx-auto leading-6">
              لا نعرض أرقامًا تقريبية أو مصادر غير مُتحقَّق منها. سيُفعَّل التبويب مباشرةً بعد اعتماد المصدر الرسمي.
            </p>
            <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.03] border border-white/10 text-white/70 text-[11px]">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
              قريبًا — بيانات مُتحقَّقة فقط
            </div>
          </div>
        </div>
      ) : (
      <>
      <div className="adv-calc__card">
        {/* Region tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-white/60 text-xs font-bold">نوع الحساب:</span>
          {REGIONS.map((r) => (
            <button
              key={r.id}
              type="button"
              data-testid={`adv-region-${r.id}`}
              onClick={() => setRegion(r.id)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-bold border transition-colors ${
                region === r.id
                  ? "bg-[#C9A96A]/15 border-[#C9A96A]/40 text-[#E5CB94]"
                  : "bg-white/[0.02] border-white/10 text-slate-300 hover:border-[#C9A96A]/25"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* Wheel select */}
        <div className="mb-3">
          <label className="text-white/70 text-[11px] font-bold mb-1 block">العجلة:</label>
          <select
            data-testid="adv-wheel"
            value={wheelId}
            onChange={(e) => handleWheelChange(e.target.value)}
            className="w-full h-11 rounded-xl bg-white/[0.03] border border-white/10 text-white px-3 text-sm focus:outline-none focus:border-[#C9A96A]/50"
          >
            {singleWheels.map((w) => (
              <option key={w.id} value={w.id} className="bg-[#0A1128]">
                {w.name} — {w.total_draws} سحبة
              </option>
            ))}
            <option disabled className="bg-[#0A1128]/50 text-[#C9A96A]">
              ── العجلات المزدوجة (صفحات مستقلة) ──
            </option>
            <option value="dual_weapons" className="bg-[#0A1128] text-[#E5CB94]">
              🔀 حاسبة العجلة المزدوجة — الأسلحة (اضغط للانتقال)
            </option>
            <option value="dual_characters" className="bg-[#0A1128] text-[#E5CB94]">
              🔀 حاسبة العجلة المزدوجة — الشخصيات (اضغط للانتقال)
            </option>
          </select>
        </div>

        {/* Completed draws input — simple default */}
        <div className="mb-3">
          <label className="text-white/70 text-[11px] font-bold mb-1 block">عدد السحبات المنفذة:</label>
          <input
            type="number"
            data-testid="adv-completed"
            min={0}
            max={totalDraws}
            value={completed}
            onChange={(e) => { setCompleted(e.target.value); setShowAdvanced(false); }}
            disabled={showAdvanced}
            className="w-full h-11 rounded-xl bg-white/[0.03] border border-white/10 text-white px-3 text-sm focus:outline-none focus:border-[#C9A96A]/50 disabled:opacity-40"
          />
          {showAdvanced && (
            <div className="text-[10px] text-amber-300 mt-1">⚠️ الوضع المتقدم مفعّل — يتم استبعاد السحبات يدويًا بالأسفل.</div>
          )}
        </div>

        {/* PLACEHOLDER — will be replaced below */}
        {false && (
          <div className="flex items-center gap-2 mb-3">
            <span>__deprecated__</span>
          </div>
        )}

        {/* Collapsible advanced customization */}
        <div className="mb-3">
          <button
            type="button"
            data-testid="adv-toggle-custom"
            onClick={() => setShowAdvanced((v) => !v)}
            className="w-full inline-flex items-center justify-between gap-2 h-10 px-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 text-white/80 text-xs font-bold transition"
          >
            <span className="flex items-center gap-2">
              <span>🎛️</span>
              <span>تخصيص السحبات (اختياري)</span>
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
          </button>
          {showAdvanced && (
            <div className="mt-2 p-3 rounded-xl bg-white/[0.02] border border-white/10">
              <div className="flex items-center justify-between mb-2">
                <label className="text-white/70 text-[11px] font-bold">اضغط على السحبة لاستبعادها:</label>
                <div className="flex gap-1">
                  <button type="button" data-testid="adv-all-on" onClick={allOn} className="text-[10px] px-2 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 font-bold">
                    تفعيل الكل
                  </button>
                  <button type="button" data-testid="adv-all-off" onClick={allOff} className="text-[10px] px-2 py-1 rounded-md bg-red-500/15 border border-red-500/40 text-red-200 font-bold">
                    استبعاد الكل
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-5 sm:grid-cols-10 gap-1.5">
                {drawStates.map((s, i) => {
                  const cost = currentWheel?.cost_per_draw?.[i];
                  return (
                    <button
                      key={i}
                      type="button"
                      data-testid={`adv-draw-${i + 1}`}
                      onClick={() => toggleDraw(i)}
                      className={`h-14 rounded-lg text-[11px] font-black border transition-colors flex flex-col items-center justify-center gap-0.5 ${
                        s?.included
                          ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-200"
                          : "bg-red-500/10 border-red-500/40 text-red-300 line-through opacity-60"
                      }`}
                      title={`سحبة ${i + 1}${cost ? ` — ${cost.toLocaleString("en-US")} CP` : ""}`}
                    >
                      <span>{i + 1}</span>
                      {cost != null && (
                        <span className="text-[9px] font-bold opacity-80">{cost.toLocaleString("en-US")}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Balance + discount */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-white/70 text-[11px] font-bold mb-1 block">رصيد CP الحالي:</label>
            <input
              type="number"
              data-testid="adv-balance"
              min={0}
              value={currentCP}
              onChange={(e) => setCurrentCP(e.target.value)}
              className="w-full h-11 rounded-xl bg-white/[0.03] border border-white/10 text-white px-3 text-sm focus:outline-none focus:border-[#C9A96A]/50"
            />
          </div>
          <div>
            <label className="text-white/70 text-[11px] font-bold mb-1 block">خصم %:</label>
            <input
              type="number"
              data-testid="adv-discount"
              min={0}
              max={100}
              value={discountPct}
              onChange={(e) => setDiscountPct(e.target.value)}
              className="w-full h-11 rounded-xl bg-white/[0.03] border border-white/10 text-white px-3 text-sm focus:outline-none focus:border-[#C9A96A]/50"
            />
          </div>
        </div>

        {error && (
          <div data-testid="adv-error" className="rounded-xl bg-red-500/10 border border-red-500/30 text-red-200 text-sm p-3 mb-3">
            {error}
          </div>
        )}

        {/* Result */}
        {loading ? (
          <div className="text-white/60 text-sm inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> جارٍ الحساب…</div>
        ) : result?.ok ? (
          <div data-testid="adv-result" className="rounded-xl bg-white/[0.03] border border-[#C9A96A]/25 p-4">
            <div className="text-white/60 text-[11px] font-bold mb-1">💎 CP المطلوب</div>
            <div className="text-[#E5CB94] text-3xl font-black" data-testid="adv-remaining">{fmt(result.remaining_cp)} <span className="text-lg">CP</span></div>
            {result.current_cp > 0 && (
              <>
                <div className="mt-2 text-white/60 text-xs">رصيدك: <span className="text-emerald-300 font-bold">{fmt(result.current_cp)} CP</span></div>
                <div className="text-white/60 text-xs">الصافي المطلوب: <span className="text-[#E5CB94] font-bold" data-testid="adv-netneeded">{fmt(result.net_needed_cp)} CP</span></div>
              </>
            )}
            {result.discount_pct > 0 && <div className="mt-1 text-white/50 text-[11px]">خصم مطبّق: {Math.round(result.discount_pct)}%</div>}

            {/* 🎯 Next Draw */}
            <div className="mt-4 pt-3 border-t border-white/10" data-testid="adv-next-draw">
              {result.included_draws && result.included_draws.length > 0 ? (
                <>
                  <div className="text-white/60 text-[11px] font-bold mb-1">🎯 السحبة القادمة</div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-white text-xl font-black" data-testid="adv-next-draw-num">
                      السحبة رقم {result.included_draws[0]}
                    </span>
                    <span className="text-[#E5CB94] text-lg font-black" data-testid="adv-next-draw-cost">
                      {fmt(result.included_costs?.[0] || 0)} CP
                    </span>
                  </div>
                </>
              ) : (
                <div className="text-emerald-300 text-sm font-black" data-testid="adv-all-done">
                  ✅ تم إكمال جميع السحبات
                </div>
              )}
            </div>

            {/* 📋 Remaining Draws List */}
            {result.included_draws && result.included_draws.length > 0 && (
              <div className="mt-3" data-testid="adv-remaining-list">
                <div className="text-white/60 text-[11px] font-bold mb-1.5">📋 السحبات المتبقية ({result.included_draws.length})</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-52 overflow-y-auto pr-1">
                  {result.included_draws.map((d, i) => (
                    <div key={d} className="flex items-center justify-between bg-white/[0.02] border border-white/8 rounded-md px-2 py-1.5">
                      <span className="text-white/70 text-[11px]">السحبة {d}</span>
                      <span className="text-[#E5CB94] text-[11px] font-black">{fmt(result.included_costs?.[i] || 0)} CP</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-3 grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
              <button data-testid="adv-add-agg" onClick={addToAggregate} className="col-span-2 sm:col-span-1 inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 text-emerald-200 text-xs font-bold transition">
                <Plus className="w-3.5 h-3.5" />
                أضف للمجموع
              </button>
              <button data-testid="adv-copy" onClick={handleCopy} className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/70 text-xs font-bold transition">
                <CopyIcon className="w-3.5 h-3.5" />
                نسخ
              </button>
              <button data-testid="adv-whatsapp" onClick={handleWhatsapp} className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg bg-[#25D366]/15 hover:bg-[#25D366]/25 border border-[#25D366]/40 text-[#B0F0C9] text-xs font-bold transition">
                <Send className="w-3.5 h-3.5" />
                واتساب
              </button>
            </div>
          </div>
        ) : null}

        {/* Suggested packages */}
        {combo?.ok && combo.combos.length > 0 && (
          <div data-testid="adv-combo" className="rounded-xl bg-white/[0.02] border border-white/10 p-4 mt-3">
            <div className="text-[#E5CB94] text-sm font-black mb-2 inline-flex items-center gap-1.5">
              <ShoppingCart className="w-4 h-4" />
              أفضل تركيبة باقات
            </div>
            {combo.combos.map((c, ci) => (
              <div key={ci} className="mb-3 last:mb-0">
                <div className="text-white/50 text-[10px] font-bold mb-1">— {c.label} —</div>
                {c.items.map((it, ii) => (
                  <div key={ii} className="text-white/80 text-xs mb-0.5">
                    {fmt(it.cp)} CP{it.qty > 1 && ` × ${it.qty}`} — <span className="text-[#E5CB94] font-bold">{fmtSar(it.sar * it.qty)}</span>
                  </div>
                ))}
                <div className="text-white/60 text-[11px] mt-1">
                  📦 {fmt(c.total_cp)} CP · 💰 {fmtSar(c.total_sar)}
                  {c.excess_cp > 0 && <span className="text-emerald-300"> · ✅ +{fmt(c.excess_cp)}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Aggregator */}
      {aggregate.length > 0 && (
        <div data-testid="adv-agg" className="adv-calc__card mt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white text-sm font-black">📚 مجمّع العجلات ({aggregate.length})</h3>
            <button data-testid="adv-agg-clear" onClick={clearAgg} className="text-[10px] px-2 py-1 rounded-md bg-red-500/15 border border-red-500/40 text-red-200 font-bold inline-flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> مسح الكل
            </button>
          </div>
          <div className="space-y-2 mb-3">
            {aggregate.map((a) => (
              <div key={a.id} className="flex items-center justify-between bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2">
                <div>
                  <div className="text-white text-sm font-bold">{a.wheelName}</div>
                  <div className="text-white/50 text-[10px]">{a.regionLabel} — {fmt(a.remaining)} CP</div>
                </div>
                <button data-testid={`adv-agg-remove-${a.id}`} onClick={() => removeAgg(a.id)} className="w-8 h-8 rounded-lg bg-white/[0.04] hover:bg-red-500/15 border border-white/10 hover:border-red-500/40 text-white/60 hover:text-red-200 flex items-center justify-center transition">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="rounded-xl bg-[#C9A96A]/[0.08] border border-[#C9A96A]/30 p-3">
            <div className="text-white/70 text-xs">إجمالي CP للمجموع</div>
            <div className="text-[#E5CB94] text-2xl font-black" data-testid="adv-agg-total">{fmt(aggTotal)} CP</div>
          </div>
          {aggCombo?.ok && aggCombo.combos.length > 0 && (
            <div className="mt-3">
              <div className="text-[#E5CB94] text-xs font-black mb-1">💎 أفضل تركيبة لتغطية المجموع</div>
              {aggCombo.combos[0].items.map((it, i) => (
                <div key={i} className="text-white/80 text-xs">
                  {fmt(it.cp)} CP{it.qty > 1 && ` × ${it.qty}`} — <span className="text-[#E5CB94]">{fmtSar(it.sar * it.qty)}</span>
                </div>
              ))}
              <div className="text-white/70 text-[11px] mt-1">📦 {fmt(aggCombo.combos[0].total_cp)} CP · 💰 {fmtSar(aggCombo.combos[0].total_sar)}</div>
            </div>
          )}
        </div>
      )}
      </>
      )}
    </section>
  );
};

export default AdvancedCalculator;
